import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

const MAX_ATTEMPTS = 10;
const INTERVAL_MS = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const attempts = useRef(0);

  useEffect(() => {
    let timer;
    if (!sessionId) {
      setState("error");
      return;
    }
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setState("paid");
          setTimeout(() => navigate("/upload", { replace: true }), 1600);
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
      } catch (e) {
        // keep polling on transient errors
      }
      if (attempts.current >= MAX_ATTEMPTS) {
        setState("timeout");
        return;
      }
      timer = setTimeout(poll, INTERVAL_MS);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, navigate, refresh]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#050914] gx-radial px-6">
      <Logo to={null} variant="icon" size="lg" className="rounded-xl" />
      <div className="mt-10 w-full max-w-md rounded-xl border border-slate-800 bg-[#0A1124] p-8 text-center" data-testid="payment-success-card">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-cyan-400" />
            <h1 className="mt-6 font-display text-2xl font-bold text-white">Confirming your payment…</h1>
            <p className="mt-2 text-slate-400">This only takes a moment. Please don't close this window.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-400" />
            <h1 className="mt-6 font-display text-2xl font-bold text-white" data-testid="payment-success-title">Payment confirmed</h1>
            <p className="mt-2 text-slate-400">Your access is ready. Taking you to a new analysis…</p>
          </>
        )}
        {state === "timeout" && (
          <>
            <Loader2 className="mx-auto h-10 w-10 text-slate-500" />
            <h1 className="mt-6 font-display text-2xl font-bold text-white">Still processing</h1>
            <p className="mt-2 text-slate-400">Your payment is taking longer than expected. Your access will update automatically once confirmed.</p>
            <div className="mt-6 flex gap-3 justify-center">
              <Button asChild variant="outline" className="border-slate-700 text-white hover:bg-slate-800 hover:text-white">
                <Link to="/dashboard" data-testid="payment-dashboard-link">Go to dashboard</Link>
              </Button>
              <Button asChild className="bg-cyan-400 text-black font-semibold hover:bg-cyan-300">
                <Link to="/billing">Back to plans</Link>
              </Button>
            </div>
          </>
        )}
        {state === "error" && (
          <>
            <AlertTriangle className="mx-auto h-12 w-12 text-amber-400" />
            <h1 className="mt-6 font-display text-2xl font-bold text-white">We couldn't confirm this payment</h1>
            <p className="mt-2 text-slate-400">No charge may have been completed. You can try again from the plans page.</p>
            <Button asChild className="mt-6 bg-cyan-400 text-black font-semibold hover:bg-cyan-300">
              <Link to="/billing" data-testid="payment-retry-link">Back to plans</Link>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
